import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useStrapi } from '@/hooks/useStrapi';
import { ServicesCategoriesCard } from '@/lib/types';
import { SubNavContainer } from '@/components/sub-nav-container';
import { ServiceAutocomplete } from '@/components/ServiceAutocomplete';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useEffect } from 'react';

export const Route = createFileRoute('/services/')({
  component: ServicesPage,
  context: () => {
    return {
      getBreadcrumbData: () => ({
        id: null,
        translationKey: null,
      }),
    };
  },
  head: () => {
    return {
      meta: [
        {
          title: 'Services - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

function ServicesPage() {
  const { t, i18n } = useTranslation();
  const analytics = useAnalytics();
  const locale = i18n.language;

  const {
    data: servicesCategories,
    isLoading: servicesCategoriesLoading,
    error: servicesCategoriesError,
  } = useStrapi<ServicesCategoriesCard>(
    'service-categories',
    locale,
    `sort=order:asc&populate[0]=services&populate[services][sort]=order:asc&populate[services][populate]=zones`,
  );

  // Track API errors
  useEffect(() => {
    if (servicesCategoriesError && analytics) {
      analytics.trackApiError('service-categories', 500);
    }
  }, [servicesCategoriesError, analytics]);

  const handleCategoryClick = (category: ServicesCategoriesCard) => {
    if (!analytics) return;

    // Track service category navigation
    analytics.trackNavigationClick(`Service Category: ${category.title}`);
  };

  if (servicesCategoriesLoading) {
    return (
      <section
        className="max-w-container-960 mx-auto px-4 lg:px-0 pt-8 pb-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        role="status"
        aria-live="polite"
      >
        {Array.from({ length: 6 }).map((_, idx) => (
          <div key={idx} className="rounded-xl shadow-md bg-white p-6 animate-pulse space-y-4" aria-hidden="true">
            <div className="h-6 bg-gray-200 rounded w-3/4" />
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
          </div>
        ))}
      </section>
    );
  }

  if (servicesCategoriesError) {
    return (
      <div className="flex justify-center mx-auto pt-8 text-red-500" role="alert">
        {t('common.errorMessage')}
      </div>
    );
  }

  return (
    <>
      <SubNavContainer showOneStepBreadcrumbs>
        <div className="w-full max-w-container-960 pb-8">
          <h1 className="text-3xl font-bold mb-5 text-foreground">{t('services.title')}</h1>
          <ServiceAutocomplete />
        </div>
      </SubNavContainer>

      <main className="max-w-container-960 mx-auto px-4 lg:px-0 pt-8 pb-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {servicesCategories && servicesCategories.length > 0 ? (
          servicesCategories.map((s) => (
            <article
              key={s.id}
              className="rounded-xl shadow-md bg-white p-6 hover:shadow-md transition"
              aria-labelledby={`service-title-${s.id}`}
            >
              <Link
                to="/services/services-catalogue"
                search={{ id: s.documentId }}
                aria-label={`${t('services.viewCategory')} ${s.title}`}
                onClick={() => handleCategoryClick(s)}
              >
                <h2
                  id={`service-title-${s.id}`}
                  className="font-bold text-link underline underline-offset-8 text-xl mb-4 hover:opacity-80 transition capitalize"
                >
                  {s.title}
                </h2>
              </Link>
              <p className="text-sm text-foreground">{s.description}</p>

              {/* Service count indicator */}
              <div className="mt-4 text-xs text-muted-foreground">
                {s.services?.length || 0} {t('services.servicesAvailable')}
              </div>
            </article>
          ))
        ) : (
          <p className="text-muted-foreground pt-8">{t('common.emptyMessage')}</p>
        )}
      </main>
    </>
  );
}
